import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import ReactTable from 'react-table';
import {Button} from 'react-bootstrap';
import {ApiContext} from '../../../api/Api';
import NewSpaAppointmentModal from './NewSpaAppointmentModal';
import SpaAppointmentDetailModal from './SpaAppointmentDetailModal';

const loadingId = 'SpaAppointments.get';

const statusLabels = {
    PENDING: 'Pendiente',
    IN_PROGRESS: 'En proceso',
    FINISHED: 'Finalizada',
    CANCELED: 'Cancelada'
};

const pad = value => String(value).padStart(2, '0');

const localDateString = date => (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
);

const timeString = value => {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
    });
};

const SpaAppointments = () => {
    const api = useContext(ApiContext);

    const [creating, setCreating] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);

    const [date, setDate] = useState(localDateString(new Date()));
    const [status, setStatus] = useState('');
    const [appointments, setAppointments] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [actionAppointmentId, setActionAppointmentId] = useState(null);

    const loadAppointments = useCallback(() => {
        setError('');

        const filters = {date};

        if (status) {
            filters.status = status;
        }

        return api.spaAppointments
            .get({
                filters,
                customProp: 'spaAppointments',
                loadingId
            })
            .then(response => {
                const data = Array.isArray(response)
                    ? response
                    : (
                        response && Array.isArray(response.data)
                            ? response.data
                            : []
                    );

                setAppointments(data);
            })
            .catch(() => {
                setAppointments([]);
                setError('No fue posible cargar la agenda de SPA.');
            });
    }, [api, date, status]);

    useEffect(() => {
        loadAppointments();
    }, [loadAppointments]);

    const executeAppointmentAction = useCallback(
        (appointment, action, successMessage, extra={}) => {
            if (!appointment || !appointment.id)
                return Promise.resolve();

            setError('');
            setSuccess('');
            setActionAppointmentId(appointment.id);

            return api.spaAppointments[action]({
                id: appointment.id,
                ...extra,
                customProp: '_spaAppointmentAction',
                loadingId: `SpaAppointments.${action}`
            })
                .then(() => {
                    setSuccess(successMessage);
                    return loadAppointments();
                })
                .catch(() => {
                    setError(
                        'No fue posible actualizar la cita. '
                        + 'El estado pudo haber cambiado.'
                    );
                })
                .finally(() => {
                    setActionAppointmentId(null);
                });
        },
        [api, loadAppointments]
    );

    const startAppointment = useCallback(
        appointment => {
            if (!window.confirm(
                '¿Deseas iniciar esta cita de SPA?'
            ))
                return;

            executeAppointmentAction(
                appointment,
                'start',
                'Cita iniciada correctamente.'
            );
        },
        [executeAppointmentAction]
    );

    const finishAppointment = useCallback(
        appointment => {
            if (!window.confirm(
                '¿Deseas finalizar esta cita de SPA?'
            ))
                return;

            executeAppointmentAction(
                appointment,
                'finish',
                'Cita finalizada correctamente.'
            );
        },
        [executeAppointmentAction]
    );

    const cancelAppointment = useCallback(
        appointment => {
            const cancellationReason = window.prompt(
                'Indica el motivo de cancelación de la cita:'
            );

            if (cancellationReason === null)
                return;

            const reason = cancellationReason.trim();

            if (!reason) {
                setSuccess('');
                setError(
                    'Debes indicar un motivo para cancelar la cita.'
                );
                return;
            }

            executeAppointmentAction(
                appointment,
                'cancel',
                'Cita cancelada correctamente.',
                {cancellationReason: reason}
            );
        },
        [executeAppointmentAction]
    );

    const columns = useMemo(() => [
        {
            Header: 'Hora',
            id: 'time',
            accessor: appointment => timeString(appointment.scheduledStartDate),
            width: 90
        },
        {
            Header: 'Huésped',
            id: 'guest',
            accessor: appointment =>
                appointment.guest ? appointment.guest.name : ''
        },
        {
            Header: 'Servicio',
            id: 'service',
            accessor: appointment =>
                appointment.service ? appointment.service.name : ''
        },
        {
            Header: 'Sala',
            id: 'lounge',
            accessor: appointment =>
                appointment.lounge ? appointment.lounge.name : ''
        },
        {
            Header: 'Asiento',
            id: 'seat',
            accessor: appointment =>
                appointment.seat || 'Sin asignar'
        },
        {
            Header: 'Cosmetólog(a)',
            id: 'staff',
            accessor: appointment =>
                appointment.staff ? appointment.staff.name : ''
        },
        {
            Header: 'Estado',
            id: 'status',
            accessor: appointment => appointment.status,
            Cell: cellInfo => {
                const appointmentStatus = cellInfo.value;

                const styles = {
                    PENDING: {
                        background: "#fff3cd",
                        color: "#856404",
                        border: "#ffeeba"
                    },
                    IN_PROGRESS: {
                        background: "#d9edf7",
                        color: "#31708f",
                        border: "#bce8f1"
                    },
                    FINISHED: {
                        background: "#dff0d8",
                        color: "#3c763d",
                        border: "#d6e9c6"
                    },
                    CANCELED: {
                        background: "#f2dede",
                        color: "#a94442",
                        border: "#ebccd1"
                    }
                };

                const style = styles[appointmentStatus] || {
                    background: "#f5f5f5",
                    color: "#555",
                    border: "#ddd"
                };

                return (
                    <span
                        style={{
                            display: "inline-block",
                            padding: "4px 10px",
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600,
                            background: style.background,
                            color: style.color,
                            border: `1px solid ${style.border}`
                        }}
                    >
                        {statusLabels[appointmentStatus] || appointmentStatus}
                    </span>
                );
            }
        },
        {
            Header: 'Acciones',
            id: 'actions',
            sortable: false,
            filterable: false,
            width: 250,
            Cell: cellInfo => {
                const appointment = cellInfo.original;
                const processing =
                    actionAppointmentId === appointment.id;

                const detailButton = (
                    <Button
                        bsStyle="default"
                        bsSize="xsmall"
                        disabled={processing}
                        onClick={() =>
                            setSelectedAppointment(appointment)
                        }
                        title="Ver detalle de la cita"
                    >
                        <i className="fa fa-eye"/>
                        {' '}
                        Detalle
                    </Button>
                );

                if (appointment.status === 'PENDING') {
                    return (
                        <div
                            style={{
                                whiteSpace: "nowrap",
                                textAlign: "center"
                            }}
                        >
                            {detailButton}

                            {' '}

                            <Button
                                bsStyle="primary"
                                bsSize="xsmall"
                                disabled={processing}
                                onClick={() =>
                                    startAppointment(appointment)
                                }
                            >
                                <i className="fa fa-play"/>
                                {' '}
                                Iniciar
                            </Button>

                            {' '}

                            <Button
                                bsStyle="danger"
                                bsSize="xsmall"
                                disabled={processing}
                                onClick={() =>
                                    cancelAppointment(appointment)
                                }
                            >
                                <i className="fa fa-times"/>
                                {' '}
                                Cancelar
                            </Button>
                        </div>
                    );
                }

                if (appointment.status === 'IN_PROGRESS') {
                    return (
                        <div
                            style={{
                                whiteSpace: "nowrap",
                                textAlign: "center"
                            }}
                        >
                            {detailButton}

                            {' '}

                            <Button
                                bsStyle="success"
                                bsSize="xsmall"
                                disabled={processing}
                                onClick={() =>
                                    finishAppointment(appointment)
                                }
                            >
                                <i className="fa fa-check"/>
                                {' '}
                                Finalizar
                            </Button>
                        </div>
                    );
                }

                return (
                    <div style={{textAlign: "center"}}>
                        {detailButton}
                    </div>
                );
            }
        }
    ], [
        actionAppointmentId,
        startAppointment,
        finishAppointment,
        cancelAppointment
    ]);

    return (
        <div className="SpaAppointments container-fluid">

            <div style={{marginBottom: 22}}>
                <h3 style={{marginBottom: 4}}>
                    Agenda SPA
                </h3>

                <p
                    className="text-muted"
                    style={{margin: 0}}
                >
                    Consulta y administra las citas programadas para los servicios del SPA.
                </p>
            </div>

            {error ? (
                <div className="alert alert-danger">
                    {error}
                </div>
            ) : null}

            {success ? (
                <div className="alert alert-success">
                    {success}
                </div>
            ) : null}

            <div
                className="panel panel-default"
                style={{
                    borderColor: "#e1e5ea",
                    boxShadow: "0 2px 5px rgba(0,0,0,.06)"
                }}
            >
                <div
                    className="panel-heading"
                    style={{
                        background: "#f7faff",
                        borderBottom: "1px solid #e1e5ea",
                        padding: "14px"
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap"
                        }}
                    >
                        <div style={{marginRight: 20}}>
                            <div
                                style={{
                                    fontSize: 16,
                                    fontWeight: 600
                                }}
                            >
                                <i
                                    className="fa fa-calendar"
                                    style={{
                                        marginRight: 9,
                                        color: "#337ab7"
                                    }}
                                />
                                Citas programadas
                            </div>

                            <div
                                className="text-muted"
                                style={{
                                    fontSize: 12,
                                    marginTop: 2
                                }}
                            >
                                Consulta la operación diaria y administra el estado de cada cita.
                            </div>
                        </div>

                        <Button
                            bsStyle="primary"
                            onClick={() => setCreating(true)}
                        >
                            <i className="fa fa-plus"/>
                            {' '}
                            Nueva cita
                        </Button>
                    </div>
                </div>

                <div
                    className="panel-body"
                    style={{
                        padding: "14px",
                        borderBottom: "1px solid #e5e5e5"
                    }}
                >
                    <div className="row">

                        <div className="col-sm-4 col-md-3">
                            <div
                                className="form-group"
                                style={{marginBottom: 0}}
                            >
                                <label>
                                    <i
                                        className="fa fa-calendar-o"
                                        style={{marginRight: 6}}
                                    />
                                    Fecha
                                </label>

                                <input
                                    type="date"
                                    className="form-control"
                                    value={date}
                                    onChange={event =>
                                        setDate(event.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div className="col-sm-4 col-md-3">
                            <div
                                className="form-group"
                                style={{marginBottom: 0}}
                            >
                                <label>
                                    <i
                                        className="fa fa-filter"
                                        style={{marginRight: 6}}
                                    />
                                    Estado
                                </label>

                                <select
                                    className="form-control"
                                    value={status}
                                    onChange={event =>
                                        setStatus(event.target.value)
                                    }
                                >
                                    <option value="">Todos</option>
                                    <option value="PENDING">
                                        Pendiente
                                    </option>
                                    <option value="IN_PROGRESS">
                                        En proceso
                                    </option>
                                    <option value="FINISHED">
                                        Finalizada
                                    </option>
                                    <option value="CANCELED">
                                        Cancelada
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div className="col-sm-4 col-md-2">
                            <div
                                className="form-group"
                                style={{marginBottom: 0}}
                            >
                                <label>&nbsp;</label>

                                <Button
                                    block
                                    onClick={loadAppointments}
                                >
                                    <i className="fa fa-refresh"/>
                                    {' '}
                                    Actualizar
                                </Button>
                            </div>
                        </div>

                    </div>
                </div>

                <ReactTable
                    data={appointments}
                    columns={columns}
                    defaultPageSize={10}
                    minRows={0}
                    noDataText="No hay citas de SPA para los filtros seleccionados."
                    style={{border: 0}}
                />
            </div>

            {creating &&
                <NewSpaAppointmentModal
                    show={creating}
                    onHide={() => setCreating(false)}
                    onCreated={() => {
                        setSuccess('Cita creada correctamente.');
                        return loadAppointments();
                    }}
                />
            }

            {selectedAppointment &&
                <SpaAppointmentDetailModal
                    show={!!selectedAppointment}
                    appointment={selectedAppointment}
                    onHide={() => setSelectedAppointment(null)}
                    onUpdated={() => {
                        setSuccess('Cita actualizada correctamente.');
                        setSelectedAppointment(null);
                        return loadAppointments();
                    }}
                />
            }
        </div>
    );
};

export default SpaAppointments;
